// Per-agent overrides of the role's run settings (`agents.run_settings_override`,
// docs/plans/2026-10-02-rapporten-contract.md §10): each switch is
// "Inherit from role" / "On" / "Off". Only the switches below are edited
// here; any other key already in the override (limits, arrays…) is kept.

import { useId } from 'react'
import type { RoleRunSettings, RunSettingsOverride } from '@goblic/shared'

type SwitchGroup = 'reports' | 'kb' | 'web' | 'delegate'

interface SwitchDef {
  group: SwitchGroup
  key: string
  label: string
  hint: string
}

const SWITCHES: SwitchDef[] = [
  {
    group: 'reports',
    key: 'read',
    label: 'Read reports',
    hint: 'Agent sees the task’s existing report and human form answers. Off keeps it unbiased by earlier decisions.',
  },
  {
    group: 'reports',
    key: 'write',
    label: 'Write reports',
    hint: 'Writes versions of the task’s report and attaches files.',
  },
  {
    group: 'kb',
    key: 'enabled',
    label: 'Knowledge base',
    hint: 'Reads and searches knowledge pages.',
  },
  {
    group: 'kb',
    key: 'write',
    label: 'Write knowledge pages',
    hint: 'Only applies while the knowledge base is on.',
  },
  {
    group: 'web',
    key: 'enabled',
    label: 'Web search and fetch',
    hint: 'Domain lists and search connection stay the role’s.',
  },
  {
    group: 'delegate',
    key: 'enabled',
    label: 'Sub-tasks',
    hint: 'Concurrency and model stay the role’s.',
  },
]

type Tri = 'inherit' | 'on' | 'off'

function overrideValue(
  override: RunSettingsOverride | null,
  group: SwitchGroup,
  key: string
): boolean | undefined {
  const block = override?.[group] as Record<string, unknown> | undefined
  const value = block?.[key]
  return typeof value === 'boolean' ? value : undefined
}

function roleValue(
  role: RoleRunSettings | null,
  group: SwitchGroup,
  key: string
): boolean | null {
  if (!role) return null
  const block = role[group] as Record<string, unknown> | undefined
  const value = block?.[key]
  return typeof value === 'boolean' ? value : null
}

/** Sets (or clears, with `undefined`) one switch; empty groups and an empty override drop out. */
export function setRunSettingsOverrideSwitch(
  override: RunSettingsOverride | null,
  group: SwitchGroup,
  key: string,
  value: boolean | undefined
): RunSettingsOverride | null {
  const next: Record<string, unknown> = { ...(override ?? {}) }
  const block: Record<string, unknown> = {
    ...((next[group] as Record<string, unknown> | undefined) ?? {}),
  }
  if (value === undefined) delete block[key]
  else block[key] = value
  if (Object.keys(block).length === 0) delete next[group]
  else next[group] = block
  return Object.keys(next).length === 0 ? null : (next as RunSettingsOverride)
}

export function RunSettingsOverrides({
  value,
  onChange,
  roleSettings,
  roleName,
}: {
  value: RunSettingsOverride | null
  onChange: (next: RunSettingsOverride | null) => void
  /** The role's current settings, for "Inherit (on/off)"; null while loading or without a role. */
  roleSettings: RoleRunSettings | null
  roleName?: string | null
}) {
  const baseId = useId()
  const count = SWITCHES.filter(
    (s) => overrideValue(value, s.group, s.key) !== undefined
  ).length

  return (
    <fieldset className="space-y-2.5">
      <legend className="text-base font-medium text-ink">Overrides</legend>
      <p className="-mt-1 text-sm text-ink-3">
        This agent follows its role’s run settings
        {roleName ? <> ({roleName})</> : null}. Override a switch here to
        differ from the role for this agent only.
      </p>
      <div className="divide-y divide-line rounded-control border border-line">
        {SWITCHES.map((s) => {
          const id = `${baseId}-${s.group}-${s.key}`
          const own = overrideValue(value, s.group, s.key)
          const inherited = roleValue(roleSettings, s.group, s.key)
          const current: Tri = own === undefined ? 'inherit' : own ? 'on' : 'off'
          const inheritedLabel =
            inherited == null ? '' : inherited ? ' (on)' : ' (off)'
          return (
            <div
              key={id}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-3.5 py-2.5"
            >
              <label htmlFor={id} className="min-w-0 flex-1">
                <span className="block text-base font-medium text-ink">
                  {s.label}
                  {own !== undefined ? (
                    <span className="ml-2 rounded-full bg-signal-soft px-1.5 text-xs font-semibold text-ink">
                      overridden
                    </span>
                  ) : null}
                </span>
                <span className="block text-sm text-ink-3">{s.hint}</span>
              </label>
              <select
                id={id}
                className="field-input w-auto py-1 text-sm"
                value={current}
                onChange={(event) => {
                  const next = event.target.value as Tri
                  onChange(
                    setRunSettingsOverrideSwitch(
                      value,
                      s.group,
                      s.key,
                      next === 'inherit' ? undefined : next === 'on'
                    )
                  )
                }}
              >
                <option value="inherit">Inherit from role{inheritedLabel}</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </div>
          )
        })}
      </div>
      {count > 0 ? (
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => {
            let next = value
            for (const s of SWITCHES) {
              next = setRunSettingsOverrideSwitch(next, s.group, s.key, undefined)
            }
            onChange(next)
          }}
        >
          Reset to the role’s settings
        </button>
      ) : null}
    </fieldset>
  )
}
